import { useRequestHeaders } from 'nuxt/app'
import { LocaleCode, isLocaleCode, normalizeLocaleCodeValue } from '@packages/i18n'
import { useAppStore } from '../stores/app'
import { COUNTRY_CODE_COOKIE_NAME, MY_COUNTRY_COOKIE_NAME, normalizeCountryCookieValue } from '../utils/countryContext'

/** 应用级默认语言，与 nuxt.config.ts 的 i18n.defaultLocale 保持同步。 */
const APP_DEFAULT_LOCALE: LocaleCode = LocaleCode.En

/**
 * 从 URL 路径中提取 locale 前缀，不依赖 Vue setup 上下文。
 * 路由策略为 prefix_except_default，默认语言 en 的路径没有前缀，
 * 提取不到 locale 时回退到 en。
 */
const extractLocaleFromPath = (fullPath: string): LocaleCode => {
  const pathOnly = fullPath.split(/[?#]/)[0] ?? ''
  const firstSegment = pathOnly.split('/')[1] ?? ''
  const normalized = normalizeLocaleCodeValue(firstSegment)

  return isLocaleCode(normalized) ? normalized : APP_DEFAULT_LOCALE
}

export type RouteTarget = {
  readonly fullPath: string
}

const initCountryContextFromCookies = (appStore: ReturnType<typeof useAppStore>) => {
  const countryCodeFromCookie = normalizeCountryCookieValue(useCookie(COUNTRY_CODE_COOKIE_NAME).value)
  const myCountryFromCookie = normalizeCountryCookieValue(useCookie(MY_COUNTRY_COOKIE_NAME).value)
  // useCookie 在路由中间件 SSR 阶段拿不到 cookie，fallback 到 server middleware 已解析的 x-country-code 请求头
  const countryCodeFromHeader = normalizeCountryCookieValue(useRequestHeaders(['x-country-code'])['x-country-code'])

  if (!appStore.CountryCode && (countryCodeFromCookie || countryCodeFromHeader)) {
    appStore.setCountryCode({ country: countryCodeFromCookie || countryCodeFromHeader })
  }

  if (!appStore.myCountry && myCountryFromCookie) {
    appStore.setMyCountry({ myCountry: myCountryFromCookie })
  }
}

export const initSharedAppHost = async (to: Readonly<RouteTarget>) => {
  const locale = extractLocaleFromPath(to.fullPath)
  const hostHeader = useRequestHeaders(['host']).host
  const initialHost = hostHeader || (typeof window !== 'undefined' ? window.location.host : '')
  const host = initialHost.includes('aws.') ? initialHost.replace('aws.', '') : initialHost
  const appStore = useAppStore()

  appStore.setHostend(host)
  appStore.setAllhost(`https://${host}${to.fullPath}`)
  appStore.setPath(to.fullPath)
  appStore.setcookieHost(host.split(':')[0] ?? host)
  appStore.setLangCode({ lang: locale, hostS: host })
  initCountryContextFromCookies(appStore)
}
